import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from 'reactstrap';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import PortRecordingTimer from './PortRecordingTimer';
import ConfirmationModal from '../ConfirmationModal';
import { useUnifiedFetch } from '../../hooks/useUnifiedFetch';
import { useUnifiedMutate } from '../../hooks/useUnifiedMutate';
import utils from '../../lib/utils';

// Helper to get OBI/ATA index from port name
const getObiIndex = (portName) => {
  const portSelected = portName.split(' ');
  return Math.round((parseInt(portSelected[1]) + 0.5) / 2) - 1;
};

const PortRecordingButton = ({ port, boxDetails, user }) => {
  // Calculate OBI index and serialNum once per render
  const obiIndex = getObiIndex(port.boxPortNumber);
  const serialNum = boxDetails.serialNumber;

  // Memoize endpoints based on current boxDetails and port
  const downloadPcapEndpoint = useMemo(() => `boxes/${boxDetails.vpnAddress}/${obiIndex}/${serialNum}/downloadpcap`, [boxDetails.vpnAddress, obiIndex, serialNum]);
  const startPcapEndpoint = useMemo(() => `boxes/${boxDetails.vpnAddress}/${obiIndex}/startpcap`, [boxDetails.vpnAddress, obiIndex]);
  const stopPcapEndpoint = useMemo(() => `boxes/${boxDetails.vpnAddress}/${obiIndex}/stoppcap`, [boxDetails.vpnAddress, obiIndex]);

  // Create fetchers at the top level (before any useEffect or logic that uses them)
  const downloadPcapFetch = useUnifiedFetch(downloadPcapEndpoint, false);
  const startPcapFetch = useUnifiedFetch(startPcapEndpoint, false);
  const stopPcapFetch = useUnifiedFetch(stopPcapEndpoint, false);

  // Hooks for API calls (endpoint must be static per hook instance)
  const { mutate: addNoteMutate } = useUnifiedMutate();

  // State and refs
  const [isRecording, setIsRecording] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [showCompletionMessage, setShowCompletionMessage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);  
  const timerRef = useRef(null);
  const [initialTimeLeftState, setInitialTimeLeftState] = useState(null);
  // Track if we've already sent stopPcap for this session
  const [stopSent, setStopSent] = useState(false);

  // Get the correct Obi and its recordingTimeStamp
  const obi = boxDetails?.Obis?.[obiIndex];
  const recordingTimeStamp = obi?.recordingTimeStamp;
  let initialTimeLeft = null;
  let shouldShowTimer = false;
  if (recordingTimeStamp) {
    const startedAt = new Date(recordingTimeStamp).getTime();
    const now = Date.now();
    const elapsed = Math.floor((now - startedAt) / 1000); // in seconds
    if (elapsed < 10 * 60) {
      initialTimeLeft = 10 * 60 - elapsed;
      shouldShowTimer = true;
    }
  }

  // Helper to get unique key for localStorage per box/port
  const getCompleteKey = () => `recordingCompleteShown_${boxDetails?._id}_${obiIndex}`;
  const getStopSentKey = () => `recordingStopSent_${boxDetails?._id}_${obiIndex}_${recordingTimeStamp}`;

  // Check if recording is complete (10 min passed, no active timer)
  let isRecordingComplete = false;
  if (recordingTimeStamp) {
    const startedAt = new Date(recordingTimeStamp).getTime();
    const now = Date.now();
    const elapsed = Math.floor((now - startedAt) / 1000); // in seconds
    if (elapsed >= 10 * 60) {
      isRecordingComplete = true;
    }
  }

  // On mount, show 'Recording Complete' if needed
  useEffect(() => {
    if (isRecordingComplete && !localStorage.getItem(getCompleteKey())) {
      setShowCompletionMessage(true);
      localStorage.setItem(getCompleteKey(), '1');
    }
  }, [isRecordingComplete, boxDetails?._id, obiIndex]);

  // Centralized stop logic to avoid duplicate stop requests
  const autoHandleStopRecording = async () => {
    const stopSentKey = getStopSentKey();
    if (stopSent || localStorage.getItem(stopSentKey)) return;
    
    setStopSent(true);
    localStorage.setItem(stopSentKey, '1');
    setIsLoading(false);
    setIsStopping(true);
    try {
      await stopPcapFetch.fetchData({}, 'GET', null, 'json');
      setIsRecording(false);
      setInitialTimeLeftState(null);
      setIsCompleted(true);
      setShowCompletionMessage(true);
      await handleDownloadPcap(); // Trigger download after stopping
    } catch (error) {
      toast.error('Error stopping recording');
    } finally {
      setIsStopping(false);
    }
  };

  // If recording is complete and stop hasn't been sent, send it
  useEffect(() => {
    if (isRecordingComplete && recordingTimeStamp && !stopSent) {
      autoHandleStopRecording();
    }
    // Reset stopSent if a new recording starts
    if (!recordingTimeStamp) {
      setStopSent(false);
      // Clear any existing stop sent flags when starting a new recording
      const stopSentKey = getStopSentKey();
      localStorage.removeItem(stopSentKey);
    }
  }, [isRecordingComplete, recordingTimeStamp, stopSent, stopPcapFetch]);

  // When starting a new recording, reset the shown flag
  const handleConfirmStartRecording = async () => {
    setShowConfirmModal(false);
    setIsLoading(true);
    setIsStopping(false);
    localStorage.removeItem(getCompleteKey());
    
    const result = await startPcapFetch.fetchData({}, 'GET', null, 'json');

    if (result && result?.status !== false) {
      setIsRecording(true);
      setInitialTimeLeftState(10 * 60); // 10 minutes in seconds
      timerRef.current = setTimeout(async () => {
        await autoHandleStopRecording();
      }, 10 * 60 * 1000); // 10 minutes
    } else {
      setIsLoading(false);
      toast.error(result?.message?.message);
    }
  };

  // Check if port is eligible for recording
  const isEligible = (() => {
    if (!port?.assignedNumberRef?.number) {
      return false;
    }
    const voiceTypes = ['voice'];
    if (voiceTypes.includes(port?.assignedNumberRef?.type)) {
      return false;
    }
    return true;
  })();

  useEffect(() => {
    // Cleanup timer on unmount
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  // Cleanup old localStorage entries on component mount
  useEffect(() => {
    // Clean up old stop sent entries that are older than 1 hour
    const oneHourAgo = Date.now() - (60 * 60 * 1000);
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('recordingStopSent_')) {
        try {
          const timestamp = key.split('_').pop();
          if (timestamp && !isNaN(timestamp) && parseInt(timestamp) < oneHourAgo) {
            localStorage.removeItem(key);
          }
        } catch (error) {
          // If parsing fails, remove the key
          localStorage.removeItem(key);
        }
      }
    });
  }, []);

  const handleStartRecording = () => {
    if (!isEligible) {
      toast.error('Port must have an assigned number and cannot be a Voice line type to enable recording.');
      return;
    }
    setShowConfirmModal(true);
  };

  // Remove direct stop logic from handleStopRecording
  const handleStopRecording = autoHandleStopRecording;

  const handleDownloadPcap = async () => {
    try {
      const response = await downloadPcapFetch.fetchData({}, 'GET', null, 'blob');
      if (response && response instanceof Blob) {
        const now = new Date();
        // ISO string, replace colons with dashes for file safety
        const timestamp = now.toISOString().replace(/:/g, '-');
        const fileName = `${serialNum}_EPI_172.16.16.25${obiIndex}_${timestamp}.pcap`;
        // Trigger download in browser
        // const url = window.URL.createObjectURL(response);
        // const link = document.createElement('a');
        // link.href = url;
        // link.setAttribute('download', fileName);
        // document.body.appendChild(link);
        // link.click();
        // link.remove();
        // window.URL.revokeObjectURL(url);

        // Create note with attachment using useUnifiedMutate (no color field)
        const formData = new FormData();
        formData.append('file', response, fileName);
        formData.append('type', 'EPI');
        formData.append('note', `EPI ${obiIndex + 1} Recording`);
        formData.append('boxId', boxDetails._id);
        await addNoteMutate({
          endpoint: `boxes/${boxDetails._id}/notes`,
          method: 'POST',
          body: formData,
        });
      } else {
        toast.error('Failed to download PCAP file.');
      }
    } catch (error) {
      toast.error('Failed to download PCAP file.');
    }
  };

  // Timer complete/cancel handlers now use the centralized stop logic
  const handleTimerComplete = async () => {
    setInitialTimeLeftState(null);
    await autoHandleStopRecording();
  };

  const handleTimerCancel = async () => {
    setInitialTimeLeftState(null);
    await autoHandleStopRecording();
  };

  // When closing the message, hide it (but don't reset localStorage)
  const handleCloseCompletionMessage = () => {
    setShowCompletionMessage(false);
  };

  if (!isEligible || !utils.filterCustomer(user.role)) {
    return null;
  }

  if (showCompletionMessage) {
    return (
      <>
        <span style={{ color: '#01cc01', fontWeight: 600, marginRight: 12 }}>
          Recording Complete
        </span>
        <Button
          color="primary"
          onClick={handleStartRecording}
          disabled={isLoading || !isEligible}
          style={{ marginRight: '10px' }}
        >
          {isLoading ? 'Starting...' : 'Enable Port Recordings'}
        </Button>
        <ConfirmationModal
          isOpen={showConfirmModal}
          onCancel={() => setShowConfirmModal(false)}
          onConfirm={handleConfirmStartRecording}
          bodyText={
            'Enabling Port Recording will capture all traffic and voice activity on this port for 10 minutes. Are you sure you want to continue?'
          }
          confirmBtnText="Continue"
          closeBtnText="Cancel"
          headerText="Attention"
          warning
        />
      </>
    );
  }

  if (shouldShowTimer || initialTimeLeftState) {
    return (
      <PortRecordingTimer
        isActive={true}
        onComplete={handleTimerComplete}
        onCancel={handleTimerCancel}
        portNumber={port.boxPortNumber}
        style={{ marginBottom: '10px' }}
        initialTimeLeft={initialTimeLeftState || initialTimeLeft}
      />
    );
  }

  return (
    <>
      <Button
        color="primary"
        onClick={handleStartRecording}
        disabled={isLoading || isStopping || !isEligible}
        style={{ marginRight: '10px' }}
      >
        {isLoading ? 'Starting...' : isStopping ? 'Stopping...' : 'Enable Port Recordings'}
      </Button>
      <ConfirmationModal
        isOpen={showConfirmModal}
        onCancel={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmStartRecording}
        bodyText={
          'Enabling Port Recording will capture all traffic and voice activity on this port for 10 minutes. Are you sure you want to continue?'
        }
        confirmBtnText="Continue"
        closeBtnText="Cancel"
        headerText="Attention"
        warning
      />
    </>
  );
};

PortRecordingButton.propTypes = {
  port: PropTypes.object.isRequired,
  boxDetails: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired
};

export default PortRecordingButton; 